import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import ProgressBar from '../components/ProgressBar';
import AIAssistant from '../components/AIAssistant';
import QuestionCard from '../components/QuestionCard';
import CollaborationPanel from '../components/CollaborationPanel';
import { useAuth } from '../hooks/useAuth.jsx';
import { useSession } from '../hooks/useSession';
import { generateSessionId } from '../utils/helpers';
import { getQuestionFlow } from '../utils/questionFlows';

const { FiUsers, FiShare2, FiSave, FiArrowLeft, FiArrowRight } = FiIcons;

const AgreementWizard = ({ onAuthRequired }) => {
  const { type } = useParams();
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  
  // Use a stable ID for this session
  const [sessionId] = useState(generateSessionId());
  
  const { session, answers, updateAnswers, loading, error, createSession } = useSession(sessionId);
  
  const [currentStep, setCurrentStep] = useState(0);
  const [collaborators, setCollaborators] = useState([]);
  const [showAI, setShowAI] = useState(false);
  const [isCollaborative, setIsCollaborative] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const questionFlow = getQuestionFlow(type);
  const totalSteps = questionFlow.length;
  const progress = ((currentStep + 1) / totalSteps) * 100;

  // Handle Authentication Redirect
  useEffect(() => {
    if (!authLoading && !user) {
      // If not logged in, trigger auth modal
      if (onAuthRequired) {
        onAuthRequired('signin');
      }
    }
  }, [authLoading, user, onAuthRequired]);

  useEffect(() => {
    // Create session if it doesn't exist in Supabase yet AND user is logged in
    if (!session && user && !loading) {
      createSession(type, sessionId);
    }
  }, [session, user, createSession, type, sessionId, loading]);

  const handleAnswer = async (questionId, answer) => {
    const newAnswers = { ...answers, [questionId]: answer };
    await updateAnswers(newAnswers);
  };

  const handleNext = () => {
    if (currentStep < totalSteps - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      // Navigate to document preview
      navigate(`/preview/${sessionId}`);
    }
  };

  const handlePrevious = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleStepClick = (stepIndex) => {
    setCurrentStep(stepIndex);
  };

  const handleInviteCollaborators = () => {
    setIsCollaborative(true);
    // Generate shareable link
    const shareLink = `${window.location.origin}/#/collaborate/${sessionId}`;
    navigator.clipboard.writeText(shareLink);
    alert('Collaboration link copied to clipboard!');
  };

  const handleSaveProgress = async () => {
    if (!user) {
      onAuthRequired('signin');
      return;
    }
    setIsSaving(true);
    // Progress is auto-saved via updateAnswers, but we can show feedback
    setTimeout(() => {
      setIsSaving(false);
      alert('Progress saved successfully!');
    }, 1000);
  };

  const currentQuestion = questionFlow[currentStep];
  const currentAnswer = answers[currentQuestion?.id];

  // Show loading state while checking auth or session
  if (authLoading || (loading && user)) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin w-12 h-12 border-4 border-blue-600 border-t-transparent rounded-full mx-auto mb-4"></div>
          <p className="text-slate-600">Loading your agreement...</p>
        </div>
      </div>
    );
  }

  // If not logged in, show simple state (will be covered by modal usually)
  if (!user) {
    return (
      <div className="min-h-screen pt-20 flex items-center justify-center">
        <div className="text-center p-8 glass-effect rounded-2xl max-w-md">
          <h2 className="text-2xl font-bold text-slate-800 mb-4">Authentication Required</h2>
          <p className="text-slate-600 mb-6">Please sign in to create and save your agreement.</p>
          <button 
            onClick={() => onAuthRequired && onAuthRequired('signin')}
            className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            Sign In
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-20 pb-10 px-4">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <div className="flex items-center justify-between mb-6">
            <button
              onClick={() => navigate('/')}
              className="flex items-center gap-2 text-slate-600 hover:text-slate-800 transition-colors"
            >
              <SafeIcon icon={FiArrowLeft} />
              Back to Home
            </button>
            <div className="flex items-center gap-4">
              {user && (
                <button
                  onClick={handleSaveProgress}
                  disabled={isSaving}
                  className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 transition-colors"
                >
                  {isSaving ? (
                    <div className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full"></div>
                  ) : (
                    <SafeIcon icon={FiSave} />
                  )}
                  {isSaving ? 'Saving...' : 'Save Progress'}
                </button>
              )}
              <button
                onClick={handleInviteCollaborators}
                className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                <SafeIcon icon={FiUsers} />
                Invite Collaborators
              </button>
              <button className="flex items-center gap-2 px-4 py-2 border border-slate-300 rounded-lg hover:bg-slate-50 transition-colors">
                <SafeIcon icon={FiShare2} />
                Share Draft
              </button>
            </div>
          </div>

          <ProgressBar progress={progress} currentStep={currentStep + 1} totalSteps={totalSteps} />

          {/* Step Navigation */}
          <div className="flex flex-wrap gap-2 mt-6">
            {questionFlow.map((question, index) => (
              <button
                key={question.id}
                onClick={() => handleStepClick(index)}
                className={`px-3 py-1 text-sm rounded-lg border transition-all ${
                  index === currentStep
                    ? 'bg-blue-600 text-white border-blue-600'
                    : answers[question.id]
                    ? 'bg-green-100 text-green-800 border-green-300 hover:bg-green-200'
                    : 'bg-slate-100 text-slate-600 border-slate-300 hover:bg-slate-200'
                }`}
                title={question.title}
              >
                {index + 1}
              </button>
            ))}
          </div>
        </motion.div>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2">
            <AnimatePresence mode="wait">
              {currentQuestion && (
                <motion.div
                  key={currentStep}
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  transition={{ duration: 0.3 }}
                >
                  <QuestionCard
                    question={currentQuestion}
                    answer={currentAnswer}
                    onAnswer={handleAnswer}
                    onAIHelp={() => setShowAI(true)}
                  />
                </motion.div>
              )}
            </AnimatePresence>

            {/* Navigation */}
            <div className="flex items-center justify-between mt-8">
              <button
                onClick={handlePrevious}
                disabled={currentStep === 0}
                className="flex items-center gap-2 px-6 py-3 border border-slate-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-slate-50 transition-colors"
              >
                <SafeIcon icon={FiArrowLeft} />
                Previous
              </button>
              <button
                onClick={handleNext}
                disabled={!currentAnswer}
                className="flex items-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-blue-700 transition-colors"
              >
                {currentStep === totalSteps - 1 ? 'Generate Documents' : 'Next'}
                <SafeIcon icon={FiArrowRight} />
              </button>
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* AI Assistant */}
            <AIAssistant
              show={showAI}
              onClose={() => setShowAI(false)}
              question={currentQuestion}
              answer={currentAnswer}
              context={{ type, answers }}
            />

            {/* Collaboration Panel */}
            {isCollaborative && (
              <CollaborationPanel
                sessionId={sessionId}
                collaborators={collaborators}
                currentStep={currentStep}
              />
            )}

            {/* Progress Summary */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="glass-effect rounded-xl p-6"
            >
              <h3 className="text-lg font-semibold text-slate-800 mb-4">Progress Summary</h3>
              <div className="space-y-3">
                <div className="flex justify-between text-sm">
                  <span className="text-slate-600">Questions Answered</span>
                  <span className="font-medium">{Object.keys(answers).length} / {totalSteps}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-slate-600">Agreement Type</span>
                  <span className="font-medium capitalize">{type}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-slate-600">Collaborators</span>
                  <span className="font-medium">{collaborators.length + (user ? 1 : 0)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-slate-600">Last Saved</span>
                  <span className="font-medium">
                    {session?.updated_at ? new Date(session.updated_at).toLocaleString() : 'Not saved'}
                  </span>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AgreementWizard;